import { Button } from '@components/Button'
import { AddAttachment } from '@components/icons/AddAttachment'
import { Input } from '@components/Input'
import { ChangeEventHandler, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { uploadAttachment } from '@/api/consultations'
import PdfIcon from '@/assets/icons/pdf.svg'
import PlaneIcon from '@/assets/icons/plane.svg'
import { AttachmentPreview } from '@/components/Consultation/steps/Landing/InputForm/AttachmentPreview'
import { Attachment } from '@/contexts/consultation'
import { useButtonClick } from '@/hooks/useButtonClick'
import { useConsultation } from '@/hooks/useConsultation'
import { useLoading } from '@/hooks/useLoading'
import { useModule } from '@/hooks/useModule'
import { useQuestion } from '@/hooks/useQuestion'
import { useStepper } from '@/hooks/useStepper'
import { useUser } from '@/hooks/useUser'
import { ACTION_TYPE } from '@/types/action'
import { BUTTON_ACTION_TYPE, BUTTON_TYPES, COMPONENT_TYPE, INPUT_COMPONENT_TYPE, PreviousAction } from '@/types/components'
import { CONSULTATION_STEPS, STEP_STEPPER } from '@/types/consultation'

import { VALID_MIME_TYPES } from './utils'

const MAX_ATTACHMENT_SIZE = 5 // 5Mb
const MAX_ATTACHMENTS = 6

export const InputForm = () => {
  const { t } = useTranslation()
  const { handleButtonClick } = useButtonClick()
  const { attachments, setAttachments } = useQuestion()
  const { user } = useUser()
  const { showLoading, hideLoading } = useLoading()
  const { currentModule } = useModule()
  const { isFollowUp } = useConsultation()
  const { updateStep } = useStepper()
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null)
  const [attachmentError, setAttachmentError] = useState<string | null>(null)

  const openAttachmentModal = () => {
    setAttachmentError(null)

    const attachmentInput = document.getElementById('attachment-input')

    if (attachmentInput) {
      attachmentInput.click()
    }
  }

  const handleAddAttachment: ChangeEventHandler<HTMLInputElement> = async (event) => {
    // If no file was selected, just return
    // (This happens when the user cancels the file selection)
    if (!event.target.files || !event.target.files.length) return

    // Check if already are {MAX_ATTACHMENTS} attachments
    if (attachments.length >= MAX_ATTACHMENTS) {
      return setAttachmentError(
        t('error.validation.attachment.max', {
          maxAmount: MAX_ATTACHMENTS
        })
      )
    }

    const file = event.target.files[0]

    // File can only be VALID_MIME_TYPES
    if (!VALID_MIME_TYPES.includes(file.type)) return setAttachmentError(t('error.validation.attachment.type'))

    const reader = new FileReader()

    reader.onloadend = async () => {
      // Check if file is already attached
      if (attachments.find((a) => a.preview === reader.result && a.file.type === file.type)) {
        return setAttachmentError(t('error.validation.attachment.duplicated'))
      }

      // Check if file is too big (>{MAX_ATTACHMENT_SIZE}Mb)
      if (file.size > MAX_ATTACHMENT_SIZE * 1024 * 1024) {
        return setAttachmentError(
          t('error.validation.attachment.size', {
            maxSize: `${MAX_ATTACHMENT_SIZE}Mb`
          })
        )
      }

      try {
        showLoading()

        const uploaded = await uploadAttachment({
          uniqueId: user?.data?.uniqueId as string,
          file: reader.result as string
        })

        setAttachments((attachments) => [...attachments, { file, preview: reader.result, url: uploaded?.url }])
      } catch (e) {
        console.error(e)
        setAttachmentError(t('error.validation.attachment.upload'))
      } finally {
        hideLoading()
      }
    }

    reader.readAsDataURL(file)
  }

  const handleRemoveAttachment = (attachment: Attachment) => {
    setAttachments(attachments.filter((a) => a.preview !== attachment.preview))
    setPreviewAttachment(null)
    setAttachmentError(null)
  }

  const openPreviewModal = (attachment: Attachment) => {
    setPreviewAttachment(attachment)
  }

  const createConsultation = async () => {
    let previousActions: PreviousAction[] | undefined

    if (isFollowUp) {
      previousActions = [
        {
          action: ACTION_TYPE.CREATE_FOLLOW_UP_CONSULTATION
        }
      ]
    } else if (currentModule?.steps?.length) {
      previousActions = [
        {
          action: ACTION_TYPE.CREATE_CONSULTATION
        }
      ]
    }

    handleButtonClick({
      type: BUTTON_ACTION_TYPE.FINISH_CONSULTATION_FORM,
      previousActions
    })
  }

  useEffect(() => {
    updateStep(STEP_STEPPER[CONSULTATION_STEPS.LANDING])
  }, [])

  return (
    <>
      <Input
        id="question"
        componentType={COMPONENT_TYPE.INPUT}
        tabIndex={0}
        type={INPUT_COMPONENT_TYPE.TEXTAREA}
        placeholder={isFollowUp ? 'follow_up.placeholder.text' : 'placeholder.text'}
        minLength={50}
        required
      />

      {attachmentError && (
        <div className="mt-4 flex flex-col items-center gap-2">
          <p className="text-sm text-red-500" data-testid="attachment-error">
            {attachmentError}
          </p>
        </div>
      )}

      <div
        className={`no-scrollbar flex w-full gap-4 overflow-x-auto ${attachments?.length > 0 ? 'mt-4 h-14' : 'h-0'} transition-all`}
        data-testid="attachments-carrousel"
      >
        {attachments?.map((attachment) => (
          <button
            key={attachment.file.name}
            onClick={() => openPreviewModal(attachment)}
            className="h-full animate-fade-in"
            id={`file-${attachment.file.name}`}
            data-testid={`file-${attachment.file.name}`}
          >
            {attachment.file.type === 'application/pdf' ? (
              <div className="flex h-full w-24 items-center justify-center rounded-md bg-gray-text">
                <img src={PdfIcon} />
              </div>
            ) : (
              <div className="h-full w-24 overflow-hidden rounded-md">
                <img src={attachment.preview as string} className="h-full w-full object-cover" />
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="flex gap-4 pt-4">
        <input
          type="file"
          className="hidden"
          id="attachment-input"
          accept={VALID_MIME_TYPES.join(',')}
          onChange={handleAddAttachment}
          onClick={(event) => {
            // This resets the input value so we can upload the same file again
            event.currentTarget.value = ''
          }}
          data-testid="attachment-input"
        />

        <Button
          type={BUTTON_TYPES.SECONDARY}
          customClassName="flex-1 px-8"
          tabIndex={1}
          onClick={openAttachmentModal}
          data-testid="attachment-button"
        >
          <AddAttachment />
        </Button>

        <Button customClassName="flex items-center justify-center" tabIndex={2} onClick={createConsultation}>
          <div className="flex gap-2">
            <img src={PlaneIcon} />
            <p className="text-white">{t('button.ask')}</p>
          </div>
        </Button>
      </div>

      {previewAttachment && (
        <AttachmentPreview
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
          onRemove={() => {
            handleRemoveAttachment(previewAttachment)
          }}
        />
      )}
    </>
  )
}
